import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { getModuleHref } from "@/modules";
import { Maximize2, PanelRightClose, SquarePen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSidebarConversations } from "@/features/conversations";
import { isDraftChatKey } from "@/features/chat/store/streaming-store";
import { useChatDock } from "../hooks/use-chat-dock";
import { DOCK_SHORTCUT_LABEL } from "../utils/shortcut";
import { ChatDockComposer } from "./ChatDockComposer";
import { ChatDockConversation } from "./ChatDockConversation";

const headerButton =
  "h-9 w-9 rounded-xl text-text-secondary dark:text-gray-400 hover:text-text-primary dark:hover:text-gray-100 hover:bg-bg-secondary dark:hover:bg-gray-800";

/**
 * Dock içeriği:
 *   başlık (modül ikonu · konuşma adı/asistan · yeni sohbet · tam sayfa · kapat)
 *   gövde  (konuşma yoksa ChatDockComposer, varsa ChatDockConversation)
 */
export const ChatDockPanel: React.FC = () => {
  const { t } = useTranslation();
  const { module, chatId, close, newConversation, expand } = useChatDock();
  const { conversations } = useSidebarConversations();

  if (!module) return null;
  const Icon = module.icon;
  const moduleName = t(module.nameKey);
  const moduleHome = getModuleHref(module, "");
  const conversationName = chatId
    ? conversations.find((c) => c._id === chatId)?.name
    : undefined;

  return (
    <div className="flex h-full min-h-0 w-full flex-col bg-bg-primary dark:bg-gray-900">
      {/* Başlık */}
      <div
        data-dock-anim
        className="flex h-14 shrink-0 items-center gap-3 border-b border-border-secondary/70 dark:border-gray-800 px-3"
      >
        <Link
          to={moduleHome}
          aria-label={moduleName}
          title={moduleName}
          className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-module-accent-soft text-module-accent transition-colors hover:bg-module-accent/20"
        >
          <Icon size={18} className="size-[18px]" />
        </Link>
        <div className="min-w-0 flex-1 leading-tight">
          <div className="truncate text-sm font-semibold text-text-primary dark:text-gray-100">
            {conversationName ?? moduleName}
          </div>
          <div className="truncate text-xs text-text-tertiary dark:text-gray-400">
            {conversationName ? moduleName : t("chatDock.title")}
          </div>
        </div>

        {chatId && (
          <>
            <Button
              variant="ghost"
              size="icon"
              onClick={newConversation}
              aria-label={t("chatDock.newChat")}
              title={t("chatDock.newChat")}
              className={headerButton}
            >
              <SquarePen className="size-[18px]" />
            </Button>
            {!isDraftChatKey(chatId) && (
              <Button
                variant="ghost"
                size="icon"
                onClick={expand}
                aria-label={t("chatDock.expand")}
                title={t("chatDock.expand")}
                className={headerButton}
              >
                <Maximize2 className="size-[18px]" />
              </Button>
            )}
          </>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={close}
          aria-label={t("chatDock.close")}
          title={`${t("chatDock.close")} · ${DOCK_SHORTCUT_LABEL}`}
          className={headerButton}
        >
          <PanelRightClose className="size-5" />
        </Button>
      </div>

      {/* Gövde — data-dock-anim: açılış/kapanışta başlıkla sıralı kayar */}
      <div data-dock-anim className="flex min-h-0 flex-1 flex-col">
        {chatId ? <ChatDockConversation /> : <ChatDockComposer />}
      </div>
    </div>
  );
};
